import { Link, useLocation } from 'react-router-dom';
import { Github, Linkedin, Twitter } from 'lucide-react';
import { ThemeToggle } from '@/components/theme-toggle';
import { Separator } from '@/components/ui/separator';

// List of public/marketing routes that should use the full footer
const PUBLIC_ROUTES = [
  '/',
  '/about',
  '/about/vision',
  '/about/company', 
  '/about/values',
  '/about/blog',
  '/professionals',
  '/employers',
  '/pricing',
  '/ai-tools',
  '/skills-verification'
];

// Helper function to check if current path matches a public route
const isPublicRoute = (pathname: string) => {
  return PUBLIC_ROUTES.some(route => {
    // Exact match
    if (route === pathname) return true;
    // Check if current path starts with route (for nested routes)
    if (route !== '/' && pathname.startsWith(route)) return true;
    return false;
  });
};

export default function Footer() {
  const location = useLocation();
  const isPublic = isPublicRoute(location.pathname);

  // Function to scroll to top
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Custom Link component that scrolls to top
  const ScrollLink = ({ to, children, className = '' }: { to: string; children: React.ReactNode; className?: string }) => (
    <Link 
      to={to} 
      className={className}
      onClick={scrollToTop}
    >
      {children}
    </Link>
  );

  if (isPublic) {
    return (
      <footer className="border-t py-6 md:py-0 bg-background">
        <div className="container px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 py-8">
            <div className="space-y-3">
              <h3 className="text-lg font-semibold">NewLoop</h3>
              <p className="text-sm text-muted-foreground">
                The modern platform for professional growth and hiring success.
              </p>
              <div className="flex items-center gap-3">
                <ScrollLink to="#" className="text-muted-foreground hover:text-foreground transition-colors">
                  <Twitter className="h-5 w-5" />
                  <span className="sr-only">Twitter</span>
                </ScrollLink>
                <ScrollLink to="#" className="text-muted-foreground hover:text-foreground transition-colors">
                  <Github className="h-5 w-5" />
                  <span className="sr-only">GitHub</span>
                </ScrollLink>
                <ScrollLink to="#" className="text-muted-foreground hover:text-foreground transition-colors">
                  <Linkedin className="h-5 w-5" />
                  <span className="sr-only">LinkedIn</span>
                </ScrollLink>
                <ThemeToggle />
              </div>
            </div>
            
            <div className="space-y-3">
              <h4 className="font-medium">Product</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <ScrollLink to="/professionals" className="text-muted-foreground hover:text-foreground transition-colors">
                    For Professionals
                  </ScrollLink>
                </li>
                <li>
                  <ScrollLink to="/employers" className="text-muted-foreground hover:text-foreground transition-colors">
                    For Employers
                  </ScrollLink>
                </li>
                <li>
                  <ScrollLink to="/pricing" className="text-muted-foreground hover:text-foreground transition-colors">
                    Pricing
                  </ScrollLink>
                </li>
              </ul>
            </div>
            
            <div className="space-y-3">
              <h4 className="font-medium">Company</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <ScrollLink to="/about" className="text-muted-foreground hover:text-foreground transition-colors">
                    About Us
                  </ScrollLink>
                </li>
                <li>
                  <ScrollLink to="/blog" className="text-muted-foreground hover:text-foreground transition-colors">
                    Blog
                  </ScrollLink>
                </li>
              </ul>
            </div>
            
            <div className="space-y-3">
              <h4 className="font-medium">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <ScrollLink to="/privacy" className="text-muted-foreground hover:text-foreground transition-colors">
                    Privacy Policy
                  </ScrollLink>
                </li>
                <li>
                  <ScrollLink to="/terms" className="text-muted-foreground hover:text-foreground transition-colors">
                    Terms of Service
                  </ScrollLink>
                </li>
                <li>
                  <ScrollLink to="/cookies" className="text-muted-foreground hover:text-foreground transition-colors">
                    Cookie Policy
                  </ScrollLink>
                </li>
              </ul>
            </div>
          </div>
          
          <Separator />
          
          <div className="py-6 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-muted-foreground">
            <p>© {new Date().getFullYear()} NewLoop. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <ScrollLink to="/privacy" className="hover:text-foreground transition-colors">
                Privacy
              </ScrollLink>
              <ScrollLink to="/terms" className="hover:text-foreground transition-colors">
                Terms
              </ScrollLink>
              <ScrollLink to="/contact" className="hover:text-foreground transition-colors">
                Contact
              </ScrollLink>
            </div>
          </div>
        </div>
      </footer>
    );
  }

  // Minimal footer for protected/app pages
  return (
    <footer className="border-t py-4 bg-background">
      <div className="container px-4 md:px-6">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} NewLoop. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <ScrollLink to="/privacy" className="hover:text-foreground transition-colors">
              Privacy
            </ScrollLink>
            <ScrollLink to="/terms" className="hover:text-foreground transition-colors">
              Terms
            </ScrollLink>
            <ScrollLink to="/contact" className="hover:text-foreground transition-colors">
              Contact
            </ScrollLink>
            <ThemeToggle />
          </div>
        </div>
      </div>
    </footer>
  );
}